import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { TrendingDown, ChevronRight, ArrowRight, Share2, DollarSign, Clock, Percent } from 'lucide-react';
import { createShareCard, copyShareLink } from '@/lib/share';

export default function WinPredictionSimulator() {
  const [budget, setBudget] = useState('200');
  const [betSize, setBetSize] = useState('5');
  const [hours, setHours] = useState([5]);
  const [gameType, setGameType] = useState('Slots');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePredict = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tools/win-prediction', { monthly_budget: parseFloat(budget), bet_size: parseFloat(betSize), hours_per_week: hours[0], game_type: gameType });
      setResult(res.data);
    } catch (err) { toast.error('Prediction failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="win-prediction-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Viral Tools</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Win Prediction</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#ef4444]/15 flex items-center justify-center shrink-0"><TrendingDown className="h-6 w-6 text-[#ef4444]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Win Prediction Simulator</h1><p className="text-[#a0a8b8] text-sm mt-1">Realistic outcome simulator - your annual gambling reality check</p></div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-[45%]">
              <div className="ciq-card">
                <form onSubmit={handlePredict} className="space-y-5">
                  <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Monthly Gambling Budget</label>
                    <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={budget} onChange={e => setBudget(e.target.value)} required min="1"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="wp-budget-input" />
                    </div>
                  </div>
                  <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Typical Bet Size</label>
                    <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={betSize} onChange={e => setBetSize(e.target.value)} required min="0.1" step="0.1"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="wp-bet-input" />
                    </div>
                  </div>
                  <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Hours Per Week: {hours[0]}</label>
                    <Slider value={hours} onValueChange={setHours} min={1} max={40} step={1} />
                  </div>
                  <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Favorite Game</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Slots', 'Blackjack', 'Roulette', 'Baccarat'].map(g => (
                        <button key={g} type="button" onClick={() => setGameType(g)}
                          className={`h-10 rounded-lg text-sm font-medium transition-colors ${gameType === g ? 'bg-[#4d7cfe] text-white' : 'bg-[#1a1f2e] border border-white/10 text-[#a0a8b8]'}`}>{g}</button>
                      ))}
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#ef4444] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="wp-predict-btn">
                    {loading ? 'Calculating...' : 'Predict My Outcomes'}
                  </button>
                </form>
              </div>
            </div>
            <div className="lg:w-[55%]">
              {result && (
                <div className="animate-slide-up space-y-4">
                  <div className="ciq-card border-[#ef4444]/30 bg-[#ef4444]/5">
                    <p className="text-xs text-[#a0a8b8] mb-1">After 1 year of ${budget}/month play...</p>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div><p className="text-xs text-[#64748b]">Total Wagered</p><p className="text-xl font-bold text-white">${result.total_wagered.toLocaleString()}</p></div>
                      <div><p className="text-xs text-[#64748b]">Expected Loss</p><p className="text-xl font-bold text-[#ef4444]">${result.expected_loss.toLocaleString()}</p></div>
                      <div><p className="text-xs text-[#64748b]">Hours of Fun</p><p className="text-lg font-bold text-white">{result.hours_of_entertainment}</p></div>
                      <div><p className="text-xs text-[#64748b]">Cost Per Hour</p><p className="text-lg font-bold text-[#f59e0b]">${result.cost_per_hour}</p></div>
                    </div>
                  </div>
                  <div className="ciq-card">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-[#a0a8b8]">Chance of finishing year ahead</p>
                      <p className="text-lg font-bold text-[#ef4444]">{result.chance_ahead_pct}%</p>
                    </div>
                    <div className="h-2 bg-[#252b3b] rounded-full"><div className="h-full bg-[#ef4444] rounded-full" style={{ width: `${result.chance_ahead_pct}%` }} /></div>
                  </div>
                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Key Insights</h3>
                    <ul className="space-y-2.5">{result.insights.map((ins, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><Percent className="h-4 w-4 text-[#4d7cfe] shrink-0 mt-0.5" />{ins}</li>
                    ))}</ul>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={async () => {
                      const card = await createShareCard('win_prediction', { monthly_budget: result.monthly_budget || 0 });
                      if (card) copyShareLink(card.share_url);
                    }}
                      className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center justify-center gap-2" data-testid="wp-share-btn">
                      <Share2 className="h-3.5 w-3.5" /> Share Reality Check
                    </button>
                    <Link to="/play-smart/gambling-budget-calculator" className="flex-1 h-10 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium flex items-center justify-center gap-2">
                      <DollarSign className="h-3.5 w-3.5" /> Budget Better
                    </Link>
                  </div>
                </div>
              )}
              {!result && <div className="ciq-card flex flex-col items-center justify-center py-12 text-center"><TrendingDown className="h-10 w-10 text-[#3a4155] mb-3" /><p className="text-[#64748b] text-sm">Run the math to see your realistic gambling outcomes</p></div>}
            </div>
          </div>
        </div>
        <CasinoSidebar sourcePage="win-prediction" />
      </div>
    </div>
  );
}
